import React, { createContext, useContext, useState, useCallback } from "react";
import { Box, Typography, IconButton, Paper, useTheme, keyframes } from "@mui/material";
import { CheckCircle, Error as ErrorIcon, Info, Warning, Close } from "@mui/icons-material";

export type ToastType = "success" | "error" | "info" | "warning";

export interface ToastItem {
    id: string;
    message: string;
    type: ToastType;
    duration: number;
}

interface ToastContextType {
    show: (message: string, type?: ToastType, duration?: number) => void;
    success: (message: string, duration?: number) => void;
    error: (message: string, duration?: number) => void;
    info: (message: string, duration?: number) => void;
    warning: (message: string, duration?: number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const slideIn = keyframes`
  from {
    transform: translateY(-20px) scale(0.95);
    opacity: 0;
  }
  to {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
`;

const shrink = keyframes`
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
`;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<ToastItem[]>([]);

    const remove = useCallback((id: string) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const show = useCallback((message: string, type: ToastType = "info", duration = 4000) => {
        const id = Math.random().toString(36).substring(2, 9);
        
        setToasts((prev) => {
            // Keep maximum of 5 toasts visible to avoid clutter
            const filtered = prev.length >= 5 ? prev.slice(1) : prev;
            return [...filtered, { id, message, type, duration }];
        });

        setTimeout(() => {
            remove(id);
        }, duration);
    }, [remove]);

    const success = useCallback((message: string, duration?: number) => show(message, "success", duration), [show]);
    const error = useCallback((message: string, duration?: number) => show(message, "error", duration), [show]);
    const info = useCallback((message: string, duration?: number) => show(message, "info", duration), [show]);
    const warning = useCallback((message: string, duration?: number) => show(message, "warning", duration), [show]);

    return (
        <ToastContext.Provider value={{ show, success, error, info, warning }}>
            {children}
            
            {/* Stacked Toasts Container */}
            <Box
                sx={{
                    position: "fixed",
                    top: { xs: 68, sm: 80 },
                    right: { xs: 12, sm: 24 },
                    left: { xs: 12, sm: "auto" },
                    zIndex: 99999,
                    display: "flex",
                    flexDirection: "column",
                    gap: { xs: 1, sm: 1.5 },
                    pointerEvents: "none",
                    maxWidth: { xs: "calc(100vw - 24px)", sm: 400 },
                    width: { xs: "auto", sm: "100%" }
                }}
            >
                {toasts.map((toast) => {
                    let icon = <Info sx={{ color: "#2196f3" }} />;
                    let accentColor = "#2196f3";
                    
                    if (toast.type === "success") {
                        icon = <CheckCircle sx={{ color: "#4caf50" }} />;
                        accentColor = "#4caf50";
                    } else if (toast.type === "error") {
                        icon = <ErrorIcon sx={{ color: "#f44336" }} />;
                        accentColor = "#f44336";
                    } else if (toast.type === "warning") {
                        icon = <Warning sx={{ color: "#ff9800" }} />;
                        accentColor = "#ff9800";
                    }

                    return (
                        <Paper
                            key={toast.id}
                            elevation={4}
                            sx={{
                                p: { xs: 1.5, sm: 2 },
                                pb: { xs: 1.8, sm: 2.2 },
                                display: "flex",
                                alignItems: "center",
                                gap: { xs: 1.25, sm: 2 },
                                borderRadius: "16px",
                                bgcolor: "rgba(20, 20, 27, 0.85)",
                                backdropFilter: "blur(12px)",
                                border: "1px solid rgba(255, 255, 255, 0.08)",
                                borderLeft: `5px solid ${accentColor}`,
                                position: "relative",
                                overflow: "hidden",
                                pointerEvents: "auto",
                                animation: `${slideIn} 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.1) forwards`,
                                boxShadow: "0 12px 32px rgba(0, 0, 0, 0.4)"
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                {icon}
                            </Box>
                            
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography 
                                    variant="body2" 
                                    sx={{ 
                                        color: "#ffffff", 
                                        fontWeight: 600,
                                        fontSize: { xs: "0.8rem", sm: "0.875rem" },
                                        lineHeight: 1.4
                                    }}
                                >
                                    {toast.message}
                                </Typography>
                            </Box>
                            
                            <IconButton 
                                size="small" 
                                onClick={() => remove(toast.id)}
                                sx={{ 
                                    color: "rgba(255, 255, 255, 0.5)",
                                    p: { xs: 0.35, sm: 0.5 },
                                    "&:hover": { color: "#ffffff", bgcolor: "rgba(255, 255, 255, 0.08)" }
                                }}
                            >
                                <Close fontSize="small" />
                            </IconButton>
                            
                            {/* Animated progress bar indicator */}
                            <Box
                                sx={{
                                    position: "absolute",
                                    bottom: 0,
                                    left: 0,
                                    height: "3px",
                                    bgcolor: accentColor,
                                    opacity: 0.8,
                                    animation: `${shrink} ${toast.duration}ms linear forwards`
                                }}
                            />
                        </Paper>
                    );
                })}
            </Box>
        </ToastContext.Provider>
    );
};

export const useToast = () => {
    const context = useContext(ToastContext);
    if (context === undefined) {
        throw new Error("useToast must be used within a ToastProvider");
    }
    return context;
};
